import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    Switch,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { ADMIN_MODULE_OPTIONS } from "../../config/adminModules";
import { useFeatures } from "../../context/FeatureContext";
import { sameFeaturePreferences } from "../../context/featurePreferences";

/**
 * Admin shell header control: Full System ↔ Admin Only, with module picker modal.
 */
export default function AdminHeader() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { currentUser } = useSelector((state) => state.user);
    const { preferences, savePreferences } = useFeatures();

    const adminId = currentUser?._id || currentUser?.id;

    const [modalOpen, setModalOpen] = useState(false);
    const [draftModules, setDraftModules] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [pendingAdminOnly, setPendingAdminOnly] = useState(false);

    const adminOnlyConfiguredRef = useRef(false);

    const isAdminOnly = pendingAdminOnly || preferences.mode === "admin-only";

    useEffect(() => {
        if (preferences.mode === "admin-only" && preferences.enabledModules.length > 0) {
            adminOnlyConfiguredRef.current = true;
        }
    }, [preferences.mode, preferences.enabledModules]);

    const persistPreferences = async (nextPreferences) => {
        setSaving(true);
        setError("");

        const result = await savePreferences(nextPreferences);

        setSaving(false);

        if (!result.ok) {
            setError(result.message || "Could not save module preferences");
            return result;
        }

        if (result.data?.mode === "admin-only" && result.data.enabledModules.length > 0) {
            adminOnlyConfiguredRef.current = true;
        }

        return result;
    };

    const openModuleModal = (seedModules = preferences.enabledModules) => {
        setDraftModules([...seedModules]);
        setError("");
        setModalOpen(true);
    };

    const handleModeToggle = async (event) => {
        const nextAdminOnly = event.target.checked;

        if (!nextAdminOnly) {
            setPendingAdminOnly(false);

            const nextPreferences = {
                mode: "full",
                enabledModules: preferences.enabledModules,
            };
            if (sameFeaturePreferences(nextPreferences, preferences)) return;
            await persistPreferences(nextPreferences);
            return;
        }

        if (!adminOnlyConfiguredRef.current) {
            setPendingAdminOnly(true);
            openModuleModal([]);
            return;
        }

        setPendingAdminOnly(false);
        const nextPreferences = {
            mode: "admin-only",
            enabledModules: preferences.enabledModules,
        };
        if (sameFeaturePreferences(nextPreferences, preferences)) return;
        await persistPreferences(nextPreferences);
    };

    const handleDraftToggle = (moduleId) => {
        setDraftModules((prev) =>
            prev.includes(moduleId)
                ? prev.filter((id) => id !== moduleId)
                : [...prev, moduleId],
        );
    };

    const handleModalClose = () => {
        if (saving) return;
        setModalOpen(false);
        setError("");
        setPendingAdminOnly(false);
    };

    const handleModalSave = async () => {
        if (draftModules.length === 0) {
            setError("Select at least one module for Admin Only mode.");
            return;
        }

        const nextPreferences = {
            mode: "admin-only",
            enabledModules: draftModules,
        };

        const result = await persistPreferences(nextPreferences);
        if (result.ok) {
            setPendingAdminOnly(false);
            setModalOpen(false);
        }
    };

    if (!adminId) return null;

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{
                    mr: 1,
                    px: { xs: 0.5, sm: 1 },
                    py: 0.25,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
                    bgcolor: alpha(theme.palette.common.white, 0.08),
                    color: "inherit",
                }}
            >
                {!isMobile && (
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "inherit", whiteSpace: "nowrap" }}
                    >
                        Full System
                    </Typography>
                )}
                <Switch
                    size="small"
                    checked={isAdminOnly}
                    onChange={handleModeToggle}
                    disabled={saving}
                    inputProps={{ "aria-label": "Toggle between Full System and Admin Only" }}
                    sx={{
                        mx: { xs: 0, sm: 0.25 },
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "common.white" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            bgcolor: alpha(theme.palette.common.white, 0.55),
                            opacity: 1,
                        },
                    }}
                />
                {!isMobile && (
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "inherit", whiteSpace: "nowrap" }}
                    >
                        Admin Only
                    </Typography>
                )}
                {isAdminOnly ? (
                    <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => openModuleModal()}
                        startIcon={<TuneOutlinedIcon fontSize="small" />}
                        sx={{
                            minWidth: 0,
                            px: { xs: 0.5, sm: 1 },
                            fontSize: "0.7rem",
                            fontWeight: 600,
                        }}
                    >
                        {isMobile ? "" : "Modules"}
                    </Button>
                ) : null}
            </Stack>

            <Dialog
                open={modalOpen}
                onClose={handleModalClose}
                fullWidth
                maxWidth="sm"
                aria-labelledby="admin-module-preferences-title"
            >
                <DialogTitle id="admin-module-preferences-title">
                    Choose modules for Admin Only mode
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Each module matches a full section in the sidebar. Disabled modules are greyed
                        out and cannot be opened.
                    </Typography>

                    {error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    ) : null}

                    <FormGroup>
                        {ADMIN_MODULE_OPTIONS.map((module) => (
                            <FormControlLabel
                                key={module.id}
                                control={
                                    <Checkbox
                                        checked={draftModules.includes(module.id)}
                                        onChange={() => handleDraftToggle(module.id)}
                                        disabled={saving}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {module.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {module.summary}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mb: 1 }}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleModalClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleModalSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? "Saving…" : "Save modules"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
